import workerpool from "workerpool";

import { chunkify, Environment, Logger } from "@abacus/common";
import {
  PortHandlerResponse,
  PortReader,
  PortServices,
  withChannel,
} from "@abacus/port-utils";

import env from "@/env";
import { Neo4JClient } from "@/neo4j";
import { ProgressTracker } from "@/progressTracker";
import { Runner, JobName, ChunkRunResult } from "@/types";
import {
  getEnvNeo4JClient,
  getProdNeo4JClient,
  getWorkerPool,
} from "@/utils/clientUtils";
import { getDupes } from "@/utils/utils";

import { createAbacusProfilesJobDataSchema } from "./schemas";
import {
  CreateAbacusProfilesJobConfig,
  CreateAbacusProfilesJobData,
  CreateAbacusProfilesJobResult,
  Email,
} from "./types";

export class CreateAbacusProfilesJob implements Runner {
  private envNeo4JClient?: Neo4JClient;
  readonly logger?: Logger;
  private pool?: workerpool.Pool;
  private prodNeo4JClient?: Neo4JClient;

  constructor(config?: CreateAbacusProfilesJobConfig) {
    this.logger = config?.logger;
  }

  async run(
    data: CreateAbacusProfilesJobData,
  ): Promise<CreateAbacusProfilesJobResult> {
    try {
      const { emails } = createAbacusProfilesJobDataSchema.parse(data);
      this.logger?.info(`Total input: ${emails.length}`);

      if (emails.length < 1) {
        return { errors: [], masked: [], success: 0, total: 0 };
      }

      return await this.createAbacusProfiles(emails);
    } finally {
      await this.terminate();
    }
  }

  private async createAbacusProfiles(
    emails: Email[],
  ): Promise<CreateAbacusProfilesJobResult> {
    const chunkSize = env.CHUNK_SIZE;
    const errors: [number, string][] = [];
    const masked: Email[] = [];
    const onBatchResult = ({
      errors: chunkErrors,
      total: chunkTotal,
      values,
    }: ChunkRunResult<boolean>): PortHandlerResponse<void> => {
      for (const [index, message] of chunkErrors) {
        errors.push([indexes[index], message]);
      }
      for (const [index, isMasked] of values) {
        if (isMasked) {
          masked.push(emails[index]);
        }
      }
      tracker.count += chunkTotal;
      tracker.errors = errors.length;
      this.logger?.info(tracker.toString());
      return { value: undefined };
    };

    this.logger?.info(`Pinging ${env.ENVIRONMENT} Neo4J...`);
    this.envNeo4JClient ??= getEnvNeo4JClient();
    const envNeo4JClient = this.envNeo4JClient;
    let status = (await envNeo4JClient.hello()).status;
    this.logger?.info(`\tAPI status: ${status}`);

    this.logger?.info(`Pinging ${Environment.Prod} Neo4J...`);
    this.prodNeo4JClient ??= getProdNeo4JClient();
    const prodNeo4JClient = this.prodNeo4JClient;
    status = (await prodNeo4JClient.hello()).status;
    this.logger?.info(`\tAPI status: ${status}`);

    const tracker = new ProgressTracker(emails.length);
    const [indexes, dupeIndexes] = getDupes(emails);
    for (const index of dupeIndexes) {
      errors.push([index, "Skipped duplicate"]);
    }
    tracker.count = errors.length;
    tracker.errors = errors.length;
    emails = indexes.map((index) => emails[index]);

    this.logger?.info(`Creating worker pool...`);
    this.pool ??= getWorkerPool(emails.length);
    this.logger?.info(`\tMax workers: ${this.pool.maxWorkers!}`);
    this.logger?.info(`\tChunk size: ${chunkSize}`);

    this.logger?.info(`Creating services...`);
    const chunks = chunkify(chunkSize, emails);
    const services = new PortServices();
    services.add("Chunks", chunks);
    services.add("EnvNeo4JClient", envNeo4JClient);
    services.add("ProdNeo4JClient", prodNeo4JClient);

    this.logger?.info("Running workers...");
    const workerPromises: Promise<void>[] = [];
    for (let i = 0; i < this.pool.maxWorkers!; ++i) {
      workerPromises.push(
        withChannel(({ port1, port2 }) => {
          services.listenOn(port1);
          new PortReader(port1).on("results", onBatchResult);
          return this.pool!.exec(JobName.CreateAbacusProfiles, [port2], {
            transfer: [port2],
          });
        }),
      );
    }

    await Promise.all(workerPromises);

    return {
      errors,
      masked,
      success: emails.length - errors.length,
      total: emails.length,
    };
  }

  private async terminate(): Promise<void> {
    const results = await Promise.allSettled([
      this.pool?.terminate(),
      this.prodNeo4JClient?.terminate(),
      this.envNeo4JClient?.terminate(),
    ]);
    this.pool = undefined;
    this.prodNeo4JClient = undefined;
    this.envNeo4JClient = undefined;
    for (const result of results) {
      if (result.status === "rejected") {
        throw result.reason;
      }
    }
  }

  async [Symbol.asyncDispose](): Promise<void> {
    this.terminate();
  }
}
